import { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useActiveProviders } from '../../hooks/queries/useProvidersQueries';
import type { Provider } from '../../stores/categories/CategoriesStore';
import { ALL_PROVIDERS_ID } from '../../stores/games-search/GamesSearchStore';
import { useStores } from '../../stores/useStores';
import Chevron from '../SVGIcons/Chevron/Chevron';

import styles from './ProvidersDropdown.module.scss';

type ProvidersDropdownProps = {
  className?: string;
  onProviderChange?: (provider: Provider) => void;
  selectedProviderId?: string;
};

export function ProvidersDropdown({
  className,
  onProviderChange,
  selectedProviderId = ALL_PROVIDERS_ID,
}: ProvidersDropdownProps) {
  const { coinTypeStore } = useStores();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: providers } = useActiveProviders();
  const { t } = useTranslation();

  const allProvidersOption: Provider = { id: ALL_PROVIDERS_ID, name: t('games.providers') };
  const isProviderSelected = selectedProviderId !== ALL_PROVIDERS_ID;
  const selectedProvider = providers?.find((p) => p.id === selectedProviderId) || allProvidersOption;
  const borderColorClass =
    coinTypeStore.coinType === 'GC' ? styles.border_gc : coinTypeStore.coinType === 'SC' ? styles.border_sc : '';
  const activeColorClass =
    coinTypeStore.coinType === 'GC'
      ? styles.listItemActiveGc
      : coinTypeStore.coinType === 'SC'
        ? styles.listItemActiveSc
        : '';

  const updatePosition = useCallback(() => {
    if (buttonRef.current && dropdownRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      dropdownRef.current.style.top = `${rect.top + window.scrollY}px`;
      dropdownRef.current.style.left = `${rect.left + window.scrollX}px`;
      dropdownRef.current.style.minWidth = `${rect.width}px`;
    }
  }, []);

  const openDropdown = () => {
    setIsOpen(true);
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleDropdown = () => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  const handleSelect = useCallback(
    (provider: Provider) => {
      onProviderChange?.(provider);
      closeDropdown();
    },
    [closeDropdown, onProviderChange],
  );

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  return (
    <div
      className={classNames(
        styles.providersDropdown,
        { [styles.providersDropdownSelected]: isProviderSelected },
        className,
      )}
    >
      <button
        ref={buttonRef}
        className={classNames(styles.triggerButton, {
          [styles.triggerButtonSelected]: isProviderSelected,
          [styles.triggerButtonOpen]: isOpen,
          [borderColorClass]: isProviderSelected,
        })}
        onClick={toggleDropdown}
      >
        <span>{selectedProvider.name}</span>
        <Chevron className={styles.chevronIcon} direction="down" />
      </button>

      {isOpen &&
        createPortal(
          <>
            <div className={styles.overlay} onClick={closeDropdown} />
            <div
              ref={dropdownRef}
              className={classNames(styles.dropdown, {
                [borderColorClass]: true,
              })}
            >
              <button className={styles.dropdownHeader} onClick={closeDropdown}>
                <span>{t('games.providers')}</span>
                <Chevron className={styles.chevronIcon} direction="up" />
              </button>

              <div className={styles.list}>
                {providers?.map((provider) => {
                  const isActive = selectedProviderId === provider.id;
                  return (
                    <button
                      key={provider.id}
                      className={classNames(styles.listItem, {
                        [styles.listItemActive]: isActive,
                        [activeColorClass]: isActive,
                      })}
                      onClick={() => handleSelect(provider)}
                    >
                      {provider.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
